import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, Button, Modal, ConfirmModal, FormInput, PasswordInput, FormSelect } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator.jsx';
import { validatePassword } from '../utils/passwordValidation';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { notify } from '@/components/core';

const UserForm = ({ roles, onSave, onCancel, policy, tenantDomain }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        roleId: roles[0]?.id || '',
        status: 'Active'
    });

    const validation = useMemo(() => validatePassword(formData.password, policy), [formData.password, policy]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });
    const [showWarning, setShowWarning] = useState(false);

    const handleSubmit = (e) => { 
        e.preventDefault(); 
        
        const newUserDomain = formData.email.split('@')[1];
        if (tenantDomain && newUserDomain && tenantDomain !== newUserDomain && !showWarning) {
            setShowWarning(true);
            return;
        }

        processSubmit();
    };

    const processSubmit = () => {
        // --- Mandatory Safety Net ---
        if (!validation.isValid) {
            notify.error(validation.errorMessage || 'Password does not meet requirements.');
            return;
        }
        console.log('[Password Validation Check] Button Disabled: NO — submit proceeding');
        onSave(formData); 
        setShowWarning(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            {/* Dummy hidden inputs to intercept browser autofill */}
            <input type="text" style={{ display: 'none' }} name="fake-email" />
            <input type="password" style={{ display: 'none' }} name="fake-password" />

            <FormInput label="Full Name" id="name" value={formData.name} onChange={handleChange} required autoComplete="off" />
            <FormInput label="Email" id="email" type="email" value={formData.email} onChange={handleChange} required autoComplete="off" />
            <PasswordInput label="Password" id="password" value={formData.password} onChange={handleChange} required autoComplete="new-password" />
            <PasswordStrengthIndicator password={formData.password} policy={policy} />
            <FormSelect label="Role" id="roleId" value={formData.roleId} onChange={handleChange}>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </FormSelect>
            <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
                <Button type="submit">Add User</Button>
            </div>

            <ConfirmModal
                isOpen={showWarning}
                onClose={() => setShowWarning(false)}
                onConfirm={processSubmit}
                title="External Domain Warning"
            >
                The email domain "<strong>{formData.email.split('@')[1]}</strong>" does not match the tenant's primary domain ("<strong>{tenantDomain}</strong>").
                <br/><br/>
                Are you sure you want to add this user to the tenant?
            </ConfirmModal>
        </form>
    );
};


const ManageUserModal = ({ user, onClose, onSave, policy }) => {
    const [password, setPassword] = useState('');
    const [status, setStatus] = useState(user.status);
    const [loading, setLoading] = useState(false);

    const validation = useMemo(() => validatePassword(password, policy), [password, policy]);

    const handleSave = async () => {
        // --- Mandatory Safety Net ---
        if (password && !validation.isValid) {
            notify.error(validation.errorMessage || 'Password does not meet requirements.');
            return;
        }
        console.log('[Password Validation Check] Button Disabled: NO — submit proceeding');
        setLoading(true);
        const updates = {};
        if (password) updates.password = password;
        if (status !== user.status) updates.status = status;

        if (Object.keys(updates).length > 0) {
            await onSave(user.id, updates);
        }
        setLoading(false);
        onClose();
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h3 className="font-medium text-gray-900 border-b pb-2">Set Password</h3>
                <div className="flex gap-2 items-end">
                    <PasswordInput
                        label="New Password"
                        id="newPassword"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password to reset"
                    />
                    {password && <PasswordStrengthIndicator password={password} policy={policy} />}
                </div>
            </div>

            <div className="space-y-2">
                <h3 className="font-medium text-gray-900 border-b pb-2">Account Status</h3>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {status}
                    </span>
                    <Button
                        variant={status === 'Active' ? 'danger' : 'secondary'}
                        onClick={() => setStatus(status === 'Active' ? 'Inactive' : 'Active')}
                        size="sm"
                    >
                        {status === 'Active' ? 'Deactivate User' : 'Activate User'}
                    </Button>
                </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="secondary" onClick={onClose}>Close</Button>
                <Button onClick={handleSave} disabled={loading || (password.length === 0 && status === user.status)}>
                    {loading ? <DynamicIcon name="Loader2" className="animate-spin h-4 w-4" /> : 'Save Changes'}
                </Button>
            </div>
        </div>
    );
};

const TenantUsersTab = ({ token, currentUser, tenantId, tenantDomain }) => {
    const fetchUrl = tenantId ? `/users?tenant_id=${tenantId}` : '/users';
    const { data: users, isLoading, error, refresh } = useData(fetchUrl, token);
    const { data: rolesData } = useData('/roles', token);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [manageUser, setManageUser] = useState(null);
    const [passwordPolicy, setPasswordPolicy] = useState(null);
    const [policyCache, setPolicyCache] = useState({});
    const fetchIdRef = React.useRef(0);
    const { formatDate } = useTimezoneFormatter();

    // Filter the roles to only show those with a 'Tenant' scope.
    const availableRoles = useMemo(() => {
        if (!rolesData) return [];
        return rolesData.filter(r => r.scope === 'Tenant');
    }, [rolesData]);

    const effectiveTenantDomain = useMemo(() => {
        if (tenantDomain) return tenantDomain;
        if (users && users.length > 0 && users[0].email) {
            return users[0].email.split('@')[1];
        }
        return '';
    }, [tenantDomain, users]);

    // Enterprise Password Policy Fetching with Caching & Race-Condition Protection
    React.useEffect(() => {
        const targetTenantId = tenantId || 'platform';
        const targetUserId = manageUser?.id;

        const fetchPolicy = async () => {
            const currentFetchId = ++fetchIdRef.current;
            
            // 1. Check Cache first
            const cacheKey = targetUserId ? `u_${targetUserId}` : `t_${targetTenantId}`;
            const cached = policyCache[cacheKey];
            
            // TTL Check (5 minutes)
            const now = dayjs().valueOf();
            if (cached && (now - cached.timestamp < 300000)) {
                setPasswordPolicy(cached.policy);
                return;
            }

            try {
                let url = `/auth/password-policy?tenant_id=${targetTenantId}`;
                if (targetUserId) url += `&user_id=${targetUserId}`;

                const res = await apiFetch(url, token);
                
                // Race condition check
                if (currentFetchId !== fetchIdRef.current) return;

                const newCacheEntry = { policy: res, timestamp: now };
                setPolicyCache(prev => ({ ...prev, [cacheKey]: newCacheEntry }));
                setPasswordPolicy(res);
            } catch (err) {
                console.error("Failed to fetch password policy", err);
            }
        };

        fetchPolicy();
    }, [tenantId, manageUser?.id, token]);

    const handleCreateUser = async (userData) => {
        try {
            const payload = { ...userData };
            if (tenantId) payload.tenantId = tenantId;

            console.log("[TenantUsersTab] Creating user with payload:", payload);

            await apiFetch('/users', token, { method: 'POST', body: JSON.stringify(payload) });
            notify.success(`User "${userData.name}" has been created.`);
            setIsAddModalOpen(false);
            refresh();
        } catch (err) {
            console.error("[TenantUsersTab] Error creating user:", err);
            notify.error(`Error creating user: ${err.message}`);
        }
    };

    const handleUpdateUser = async (userId, updates) => {
        try {
            await apiFetch(`/users/${userId}`, token, { method: 'PUT', body: JSON.stringify(updates) });
            notify.success('User updated successfully.');
            refresh();
        } catch (err) {
            notify.error(`Error updating user: ${err.message}`);
        }
    };

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-red-500 p-4 bg-red-50 rounded-md">{error.message}</div>;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Manage Users</h2>
                <Button onClick={() => setIsAddModalOpen(true)}>
                    <DynamicIcon name="PlusCircle" className="mr-2 h-4 w-4" />
                    Add User
                </Button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">User</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Role</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Joined</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {users?.map(u => (
                            <tr key={u.id}>
                                <td className="p-4">
                                    <div className="font-medium">{u.name}</div>
                                    <div className="text-sm text-gray-500">{u.email}</div>
                                </td>
                                <td className="p-4">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{u.status}</span>
                                </td>
                                <td className="p-4 text-sm">{u.role}</td>
                                <td className="p-4 text-xs text-gray-500 dark:text-gray-400">
                                    {formatDate(u.createdAt || u.created_at)}
                                </td>
                                <td className="p-4">
                                    <Button variant="secondary" size="sm" onClick={() => setManageUser(u)}>
                                        <DynamicIcon name="Settings" className="w-4 h-4 mr-1" />
                                        Manage
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add User Modal */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New User">
                <UserForm roles={availableRoles} onSave={handleCreateUser} onCancel={() => setIsAddModalOpen(false)} policy={passwordPolicy} tenantDomain={effectiveTenantDomain} />
            </Modal>

            {/* Manage User Modal */}
            <Modal isOpen={!!manageUser} onClose={() => setManageUser(null)} title={`Manage User: ${manageUser?.name}`}>
                {manageUser && (
                    <ManageUserModal
                        user={manageUser}
                        onClose={() => setManageUser(null)}
                        onSave={handleUpdateUser}
                        policy={passwordPolicy}
                    />
                )}
            </Modal>
        </div>
    );
};

export default TenantUsersTab;